'use client';
import PlaylistIconSvg from '@/assets/svgs/text-editor/playlist-02.svg';
import { MediaItem } from '@/containers/MediaManager/types';
import { getShortcutModifier, isMac } from '@/shared/utils/window';
import { Editor } from '@tiptap/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import useTextEditorShortcuts from '../hooks/useTextEditorShortcuts';
import KeyboardShortcutText from './KeyboardShortcutText';
import MediaManagerDropdownView from './MediaManagerDropdownView';
import { useAppDispatch } from '@/lib/redux/store';
import { setIsShowingMediaManager } from '@/containers/Jobs/redux/jobEditors.slice';

interface InsertMediaDropdownButtonProps {
  className?: string;
  editor: Editor | null;
  isOpen?: boolean;
  jobId?: string;
  onMediaSelected?: (media: MediaItem) => void;
}

const InsertMediaDropdownButton = ({
  className,
  editor,
  isOpen: isOpenProp,
  jobId = '',
  onMediaSelected,
}: InsertMediaDropdownButtonProps) => {
  const dispatch = useAppDispatch();
  const { closeFloatingItems } = useTextEditorShortcuts(jobId);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [isClickOpen, setIsClickOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleHoverEnter = () => {
    setIsHoverOpen(true);
    setIsClickOpen(false); // Close click dropdown when hover opens
  };

  const handleHoverLeave = () => {
    setIsHoverOpen(false);
  };

  const handleClickToggle = () => {
    const willOpen = !isClickOpen;

    closeFloatingItems();
    setIsClickOpen(willOpen);
    setIsHoverOpen(false); // Close hover dropdown when click toggles

    // Sync with Redux to prevent useEffect from closing it immediately
    // due to closeFloatingItems() setting it to false
    if (willOpen) {
      dispatch(setIsShowingMediaManager({ jobId, isShowing: true }));
    }

    if (editor && willOpen) {
      editor.commands.blur();
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isClickOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsClickOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClickOpen]);

  // Sync with external isOpen prop (for closing when editor focuses)
  useEffect(() => {
    if (isOpenProp === false && isClickOpen) {
      setIsClickOpen(false);
    }
  }, [isOpenProp, isClickOpen]);

  return (
    <div
      ref={containerRef}
      className={classNames(
        'dropdown',
        {
          'dropdown-top dropdown-hover': isHoverOpen && !isClickOpen,
          'dropdown-bottom dropdown-open': isClickOpen,
        },
        className
      )}
      data-dropdown-dismiss="true">
      <div
        tabIndex={0}
        role="button"
        data-button-id="editor-toolbar-insert-media-button"
        className="btn btn-square btn-ghost btn-sm"
        onMouseDown={(e) => {
          e.preventDefault(); // Prevent button from stealing focus from editor
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleClickToggle();
        }}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}>
        <PlaylistIconSvg />
      </div>
      {isHoverOpen && !isClickOpen && (
        <div
          tabIndex={0}
          className="dropdown-content bg-black-default/80 z-[1] mb-0.5 !flex h-[26px] min-w-[158px] items-center justify-center gap-1.5 rounded-md px-[8px] whitespace-nowrap text-white">
          <div className="text-[12px]">Insert Media</div>
          <div className="shortcuts flex gap-1">
            <KeyboardShortcutText
              shortcut={getShortcutModifier()}
              className="text-[16px]"
            />
            <KeyboardShortcutText
              shortcut="⇧"
              className={isMac() ? 'pb-0.5 text-[16px]' : 'text-[12px]'}
            />
            <KeyboardShortcutText shortcut="K" className="text-[12px]" />
          </div>
        </div>
      )}
      {isClickOpen && (
        <MediaManagerDropdownView
          anchorRef={containerRef as unknown as React.RefObject<HTMLElement>}
          onClose={() => {
            setIsClickOpen(false);
          }}
          jobId={jobId}
          onMediaSelected={onMediaSelected}
        />
      )}
    </div>
  );
};

export default InsertMediaDropdownButton;
